import React, { useEffect, useMemo, useState, useCallback, memo } from "react";
import {
  Container,
  Grid,
  FormLabel,
  TextField,
  Box,
  Button,
  Menu,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
  Typography,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import {
  CommentType,
  ProductHelpCommentType,
  ImpactProgramsCommentType,
  ContactUsDto,
  WarehouseModel,
} from "../../models/contact-us/types";
import dataService from "../../services/dataService";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useLocation } from "react-router-dom";
import DOMPurify from "dompurify";

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, opts: { action: string }): Promise<string>;
    };
  }
}

type Props = {
  warehouseCode?: string;
};

interface FormData {
  firstName: string;
  lastName: string;
  accountCompany: string;
  phone: string;
  email: string;
}
const formatPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "");

  if (digits.length !== 10) return phone;

  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const TOPIC_OPTIONS: { value: CommentType; label: string }[] = [
  { value: "OnlineStoreComment", label: "Product Help" },
  { value: "ImpactProgramsComment", label: "IMPACT Programs" }, // NEW
  { value: "LansingGreenComment", label: "Financing Solutions" },
  {
    value: "LansingMarketingServicesComment",
    label: "Lansing Marketing Services",
  },
  { value: "BillTrustInvoicesComment", label: "BillTrust/Invoices" },
  { value: "DeliveredExcellenceApp", label: "Delivery App" },
  { value: "OtherComment", label: "Other" },
];

const onlyDigits = (s: string) => s.replace(/\D/g, "");
const formatDashedPhone = (digits: string) => {
  const d = digits.slice(0, 10);
  const a = d.slice(0, 3);
  const b = d.slice(3, 6);
  const c = d.slice(6, 10);
  if (d.length <= 3) return a;
  if (d.length <= 6) return `${a}-${b}`;
  return `${a}-${b}-${c}`;
};
const isDashedPhoneValid = (val: string) => /^\d{3}-\d{3}-\d{4}$/.test(val);

const baseText = {
  fontSize: "14px",
  fontFamily: `"Montserrat", Verdana, sans-serif`,
} as const;

const fieldRowSx = {
  position: "relative" as const,
  mx: "auto",
  overflow: "visible",
};

const noticeTextSx = {
  fontSize: "16px",
  fontFamily: `"Montserrat", Verdana, sans-serif`,
  color: "#494440",
  fontWeight: 400,
  lineHeight: 1.6,
} as const;

const linkSx = {
  ...baseText,
  color: "#62b5e5",
  fontWeight: 700,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

const errorTextSx = {
  ...baseText,
  color: "#af272f",
  mt: 0.5,
} as const;

// ---- Memoized BorderedInput to avoid remounts ----
type BorderedInputProps = {
  name: keyof FormData | string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  required?: boolean;
  type?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
};

const BorderedInput = memo(function BorderedInput({
  name,
  value,
  onChange,
  required,
  type = "text",
  inputProps,
  onKeyDown,
  onPaste,
}: BorderedInputProps) {
  return (
    <Box
      sx={{
        borderWidth: 1,
        borderColor: "#d9d9d9",
        borderStyle: "solid",
        px: "10px",
        borderRadius: "4px",
      }}
    >
      <TextField
        variant="standard"
        fullWidth
        name={name as string}
        type={type}
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        InputProps={{
          disableUnderline: true,
          sx: {
            ...baseText,
            color: "#000",
            height: "32px",
            "& input": {
              fontSize: "14px",
            },
            inputProps: inputProps,
          },
        }}
      />
    </Box>
  );
});

export default function ContactUs() {
  const query = useQuery();
  let warehouseCode = query.get("warehouseCode")
    ? DOMPurify.sanitize(query.get("warehouseCode")!)
    : null;

  const [siteKey, setSiteKey] = useState<string | undefined>(undefined);

  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    accountCompany: "",
    phone: "",
    email: "",
  });

  const [topic, setTopic] = useState<CommentType | "">("");
  const [comment, setComment] = useState<string>("");
  const [productHelpType, setProductHelpType] = useState<
    ProductHelpCommentType | ""
  >("");
  const [impactProgramsType, setImpactProgramsType] = useState<
    ImpactProgramsCommentType | ""
  >("");
  const [warehouse, setWarehouse] = useState<WarehouseModel | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [toast, setToast] = useState<{
    open: boolean;
    msg: string;
    sev: "success" | "error";
  }>({
    open: false,
    msg: "",
    sev: "success",
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const [errors, setErrors] = useState<{
    accountCompany?: string;
    email?: string;
    phone?: string;
    topic?: string;
    comment?: string;
    productHelpType?: string;
    impactProgramsType?: string;
  }>({});

  const urlWarehouseCode = useMemo(() => {
    if (typeof window === "undefined") return "";
    const params = new URLSearchParams(window.location.search);
    return params.get("warehouseCode") ?? "";
  }, []);

  const [sent, setSent] = useState(false);

  const location = useLocation();
  useEffect(() => {
    setSent(false);
    setErrors({});
    setTopic("");
    setProductHelpType("");
    setImpactProgramsType("");
    setComment("");
  }, [location.key]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const candidate = (warehouseCode ?? urlWarehouseCode)?.trim() || null;
        const resp = await dataService.getContactUs(candidate);
        const contactData: ContactUsDto | undefined = resp?.data;

        if (mounted && contactData) {
          setFormData({
            firstName: contactData.firstName || "",
            lastName: contactData.lastName || "",
            accountCompany: contactData.isSapSource
              ? contactData.sapCustomerName || ""
              : contactData.companyName || "",
            phone: contactData.contactPhone
              ? formatDashedPhone(onlyDigits(contactData.contactPhone))
              : "",
            email: contactData.contactEmail || "",
          });
          setComment(contactData.comment || "");
          setTopic("");
          setProductHelpType("");
          setImpactProgramsType("");
          setWarehouse(contactData.warehouse || null);

          setSiteKey((contactData as any).captchaV3PublicKey || undefined);
        }
      } catch (e: any) {
        console.error("getContactUs failed:", e);
        if (mounted) {
          setToast({
            open: true,
            msg: e?.message || "Failed to load contact form.",
            sev: "error",
          });
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [warehouseCode, urlWarehouseCode, location.key]);

  const ensureRecaptchaReady = useCallback(async (key?: string) => {
    if (!key) throw new Error("reCAPTCHA site key is not configured.");
    if (typeof window === "undefined")
      throw new Error("reCAPTCHA unavailable on server.");

    if (!window.grecaptcha) {
      const id = `grecaptcha-v3-${key}`;
      if (!document.getElementById(id)) {
        const s = document.createElement("script");
        s.id = id;
        s.async = true;
        s.defer = true;
        s.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(
          key
        )}`;
        const loaded = new Promise<void>((resolve, reject) => {
          s.onload = () => resolve();
          s.onerror = () =>
            reject(new Error("Failed to load reCAPTCHA script."));
        });
        document.head.appendChild(s);
        await loaded;
      } else {
        await new Promise((r) => setTimeout(r, 0));
      }
    }

    if (window.grecaptcha) {
      await new Promise<void>((resolve) =>
        window.grecaptcha!.ready(() => resolve())
      );
    } else {
      throw new Error("reCAPTCHA failed to initialize.");
    }
  }, []);

  // Restrict key presses to digits and control keys for phone input
  const handlePhoneKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const allowedControl = [
        "Backspace",
        "Delete",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Tab",
      ];
      if (allowedControl.includes(e.key)) return;

      // allow Cmd/Ctrl + C/V/X/A
      if (
        (e.metaKey || e.ctrlKey) &&
        ["a", "c", "v", "x"].includes(e.key.toLowerCase())
      )
        return;

      // digits only
      if (!/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    },
    []
  );

  const handlePhonePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text");
      const digits = onlyDigits(pasted);
      const formatted = formatDashedPhone(digits);
      setFormData((prev) => ({ ...prev, phone: formatted }));
      if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
    },
    [errors.phone]
  );

  // stable onChange so children don’t think they’re new every render
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      if (name === "phone") {
        const digits = onlyDigits(value);
        const formatted = formatDashedPhone(digits);
        setFormData((prev) => ({ ...prev, phone: formatted }));
        if (errors.phone) setErrors((prev) => ({ ...prev, phone: undefined }));
        return;
      }

      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === "accountCompany" && errors.accountCompany)
        setErrors((prev) => ({ ...prev, accountCompany: undefined }));
      if (name === "email" && errors.email)
        setErrors((prev) => ({ ...prev, email: undefined }));
    },
    [errors.accountCompany, errors.email, errors.phone]
  );

  const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const handleChooseTopic = (value: CommentType) => {
    setTopic(value);
    setAnchorEl(null);
    setErrors((prev) => ({
      ...prev,
      topic: undefined,
      productHelpType: undefined,
      impactProgramsType: undefined,
    }));
    if (value !== "OnlineStoreComment") setProductHelpType("");
    if (value !== "ImpactProgramsComment") setImpactProgramsType("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: typeof errors = {};
    if (!formData.accountCompany.trim())
      newErrors.accountCompany = "Enter Account Number/Company Name.";
    if (!formData.email.trim()) newErrors.email = "Enter contact Email.";

    if (formData.phone.trim() && !isDashedPhoneValid(formData.phone.trim())) {
      newErrors.phone = "Enter a valid Phone (###-###-####).";
    }

    if (!topic) {
      newErrors.topic = "Comment Type is needed.";
    } else if (!comment.trim()) {
      newErrors.comment = "Comment is needed.";
    } else if (topic === "OnlineStoreComment" && !productHelpType) {
      newErrors.productHelpType = "Choose Help Type";
    } else if (topic === "ImpactProgramsComment" && !impactProgramsType) {
      newErrors.impactProgramsType = "Choose Help Type";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      setSubmitting(true);

      // 1) Ensure reCAPTCHA for backend-provided siteKey, then get token
      await ensureRecaptchaReady(siteKey);
      const captchaToken = await window.grecaptcha!.execute(siteKey!, {
        action: "contactus",
      });

      // 2) Build payload to match backend property names
      const payload: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactPhone: onlyDigits(formData.phone),
        contactEmail: formData.email,
        companyName: formData.accountCompany,
        comment,
        commentType: (topic as CommentType) || "OtherComment",

        onlineStoreComment:
          topic === "OnlineStoreComment"
            ? (productHelpType as ProductHelpCommentType) || "Other"
            : "Other",
        customerProgramsCommentType:
          topic === "ImpactProgramsComment"
            ? ((impactProgramsType || "None") as ImpactProgramsCommentType)
            : "None",

        warehouse: warehouse || undefined,
        captchaToken: captchaToken,
      };

      const resp = await dataService.sendContactUs(payload as ContactUsDto);

      if (resp?.success) {
        setSent(true);
      } else {
        console.error(resp?.message || "Failed to send message.");
      }
    } catch (err: any) {
      console.error("sendContactUs failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedTopicLabel = useMemo(
    () => TOPIC_OPTIONS.find((o) => o.value === topic)?.label || "",
    [topic]
  );

  return (
    <>
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#62b5e5",
          marginBottom: "50px",
          minHeight: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: "#fff",
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: 600,
            fontSize: "36px",
          }}
        >
          Contact Us
        </Typography>
      </Box>

      {sent ? (
        <Container maxWidth="sm" sx={{ mt: 2 }}>
          <div
            style={{
              display: "flex",
              minHeight: "40vh",
              alignItems: "center",
              justifyContent: "center",
              paddingBottom: "20px",
              fontFamily: "'Montserrat', Verdana, sans-serif",
              fontSize: "20px",
              color: "#000",
              textAlign: "center",
            }}
          >
            Your inquiry has been successfully sent.
          </div>

          <Snackbar
            open={toast.open}
            autoHideDuration={4000}
            onClose={() => setToast((t) => ({ ...t, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.sev}
              onClose={() => setToast((t) => ({ ...t, open: false }))}
              variant="filled"
              sx={{ ...baseText }}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Container>
      ) : (
        <Box
          sx={{
            mt: 2,
            px: 4,
            mx: "auto",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={5.5}>
              <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                Get in touch with LansingNOW
              </Typography>
              <Typography sx={{ mt: 2 }}>
                For questions about your Lansing account, LansingNOW, or
                customer programs, please complete the form below or call{" "}
                <span style={{ fontWeight: "bold" }}>888-631-0085.</span>
              </Typography>

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
                <Grid container spacing={3}>
                  {(
                    [
                      "firstName",
                      "lastName",
                      "accountCompany",
                      "phone",
                      "email",
                    ] as const
                  ).map((field) => {
                    const labelMap: Record<string, string> = {
                      firstName: "First Name:",
                      lastName: "Last Name:",
                      accountCompany: "Account Number - Company Name:",
                      phone: "Contact Phone:",
                      email: "Contact Email:",
                    };
                    const required =
                      field === "accountCompany" || field === "email";
                    const type = field === "phone" ? "tel" : "text";

                    const phoneSpecificProps =
                      field === "phone"
                        ? {
                            inputProps: {
                              inputMode: "numeric",
                              pattern: "\\d{3}-\\d{3}-\\d{4}",
                              maxLength: 12,
                            } as React.InputHTMLAttributes<HTMLInputElement>,
                            onKeyDown: handlePhoneKeyDown,
                            onPaste: handlePhonePaste,
                          }
                        : {};

                    return (
                      <Grid item xs={12} key={field}>
                        <FormLabel
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            mb: 1,
                            color: "#494440",
                            mx: "auto",
                            ...baseText,
                          }}
                        >
                          {labelMap[field]}
                          {required && (
                            <Box
                              component="span"
                              sx={{
                                color: "red",
                                padding: 1,
                                fontSize: "16px",
                                lineHeight: 1,
                              }}
                            >
                              *
                            </Box>
                          )}
                        </FormLabel>

                        <BorderedInput
                          name={field}
                          value={formData[field]}
                          onChange={handleChange}
                          required={required}
                          type={type}
                          {...phoneSpecificProps}
                        />

                        {field === "accountCompany" &&
                          errors.accountCompany && (
                            <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                              {errors.accountCompany}
                            </Typography>
                          )}
                        {field === "email" && errors.email && (
                          <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                            {errors.email}
                          </Typography>
                        )}
                        {field === "phone" && errors.phone && (
                          <Typography sx={{ ...errorTextSx, mx: "auto" }}>
                            {errors.phone}
                          </Typography>
                        )}
                      </Grid>
                    );
                  })}

                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        width: "100%",
                      }}
                    >
                      <FormLabel
                        sx={{
                          display: "flex",
                          justifyContent: "start",
                          fontWeight: 600,
                          mb: 1,
                          color: "#494440",
                          ...baseText,
                        }}
                      >
                        What can we help with?
                      </FormLabel>

                      <Button
                        fullWidth
                        onClick={handleOpenMenu}
                        endIcon={<ArrowDropDownIcon />}
                        sx={{
                          flex: 1,
                          height: "36px",
                          border: "1.75px solid #d9d9d9",
                          borderRadius: "4px",
                          padding: "4px 10px",
                          backgroundColor: "transparent",
                          color: "#494440",
                          textTransform: "none",
                          fontWeight: 500,
                          justifyContent: "start",
                        }}
                      >
                        {selectedTopicLabel || "Please select"}
                      </Button>

                      <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleCloseMenu}
                        keepMounted
                      >
                        {TOPIC_OPTIONS.map((opt) => (
                          <MenuItem
                            key={opt.value}
                            onClick={() => handleChooseTopic(opt.value)}
                            selected={topic === opt.value}
                            sx={{
                              color: "#494440",
                              ...baseText,
                            }}
                          >
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Menu>

                      {errors.topic && (
                        <Typography
                          sx={{
                            ...errorTextSx,
                            mt: 1,
                            textAlign: "center",
                          }}
                        >
                          {errors.topic}
                        </Typography>
                      )}
                    </Box>
                  </Grid>

                  {topic && (
                    <Grid item xs={12}>
                      {/* Shared Comment field (same for all topics) */}
                      <Box sx={{ mb: 2 }}>
                        <FormLabel
                          sx={{
                            display: "block",
                            fontWeight: 600,
                            mb: 1,
                            color: "#494440",
                            width: "100%",
                            mx: "auto",
                            ...baseText,
                          }}
                        >
                          {`${selectedTopicLabel} Comment`}

                          <Box
                            component="span"
                            sx={{
                              color: "red",
                              padding: 1,
                              fontSize: "16px",
                              lineHeight: 1,
                            }}
                          >
                            *
                          </Box>
                        </FormLabel>
                        <Box>
                          <Box
                            sx={{
                              width: "100%",
                              border: "1px solid #d9d9d9",
                              backgroundColor: "transparent",
                            }}
                          >
                            <TextField
                              variant="standard"
                              fullWidth
                              multiline
                              minRows={15}
                              value={comment}
                              onChange={(e) => {
                                setComment(e.target.value);
                                if (errors.comment)
                                  setErrors((prev) => ({
                                    ...prev,
                                    comment: undefined,
                                  }));
                              }}
                              InputProps={{
                                disableUnderline: true,
                                sx: {
                                  ...baseText,
                                  color: "#666",
                                  resize: "none",
                                  px: 1,
                                  backgroundColor: "transparent",
                                },
                              }}
                            />
                          </Box>
                        </Box>
                        {errors.comment && (
                          <Typography
                            sx={{ ...errorTextSx, width: "80%", mx: "auto" }}
                          >
                            {errors.comment}
                          </Typography>
                        )}
                      </Box>

                      {/* Product Help radios */}
                      {topic === "OnlineStoreComment" && (
                        <Box sx={{ mb: 2 }}>
                          <FormLabel
                            sx={{
                              display: "block",
                              fontWeight: 600,
                              mb: 1,
                              color: "#494440",
                              mx: "auto",
                              ...baseText,
                            }}
                          >
                            Product Help Comment Type:
                          </FormLabel>
                          <Box>
                            <Box
                              sx={{
                                width: "100%",
                                border: "1px solid #d9d9d9",
                                backgroundColor: "transparent",
                              }}
                            >
                              <RadioGroup
                                name="OnlineStoreCommentType"
                                value={productHelpType}
                                onChange={(e) => {
                                  setProductHelpType(
                                    e.target.value as ProductHelpCommentType
                                  );
                                  if (errors.productHelpType)
                                    setErrors((prev) => ({
                                      ...prev,
                                      productHelpType: undefined,
                                    }));
                                }}
                              >
                                {[
                                  {
                                    label: "Product you can't find",
                                    value: "CannotFindItem",
                                  },
                                  {
                                    label: "Loading Error or Tech Error",
                                    value: "LoadingOrTechError",
                                  },
                                  { label: "Other", value: "Other" },
                                ].map((item) => (
                                  <Box
                                    key={item.value}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                    }}
                                  >
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        component="label"
                                        sx={{
                                          ...baseText,
                                          color: "#000",
                                          fontWeight: 400,
                                          m: 0,
                                          pl: 1,
                                        }}
                                      >
                                        {item.label}
                                      </Typography>
                                    </Box>
                                    <FormControlLabel
                                      sx={{ m: 0 }}
                                      value={item.value}
                                      control={<Radio />}
                                      label=""
                                    />
                                  </Box>
                                ))}
                              </RadioGroup>
                            </Box>
                          </Box>
                          {errors.productHelpType && (
                            <Typography
                              sx={{ ...errorTextSx, mx: "auto", mt: 1 }}
                            >
                              {errors.productHelpType}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* IMPACT Programs radios */}
                      {topic === "ImpactProgramsComment" && (
                        <Box sx={{ mb: 2 }}>
                          <FormLabel
                            sx={{
                              display: "block",
                              fontWeight: 600,
                              mb: 1,
                              color: "#494440",
                              mx: "auto",
                              ...baseText,
                            }}
                          >
                            IMPACT Programs Comment Type:
                          </FormLabel>
                          <Box>
                            <Box
                              sx={{
                                width: "100%",
                                border: "1px solid #d9d9d9",

                                backgroundColor: "transparent",
                              }}
                            >
                              <RadioGroup
                                name="ImpactProgramsCommentType"
                                value={impactProgramsType}
                                onChange={(e) => {
                                  setImpactProgramsType(
                                    e.target.value as ImpactProgramsCommentType
                                  );
                                  if (errors.impactProgramsType)
                                    setErrors((prev) => ({
                                      ...prev,
                                      impactProgramsType: undefined,
                                    }));
                                }}
                              >
                                {[
                                  {
                                    label: "Loyalty Link",
                                    value: "LoyaltyLink",
                                  },
                                  {
                                    label: "IMPACT Rewards",
                                    value: "ImpactRewards",
                                  },
                                  {
                                    label: "Lead Generation",
                                    value: "LeadGeneration",
                                  },
                                ].map((item) => (
                                  <Box
                                    key={item.value}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      py: 0.5,
                                      ml: 1,
                                    }}
                                  >
                                    <Box sx={{ flex: 1, pr: 2 }}>
                                      <Typography
                                        component="label"
                                        sx={{
                                          ...baseText,
                                          color: "#000",
                                          fontWeight: 400,
                                          m: 0,
                                        }}
                                      >
                                        {item.label}
                                      </Typography>
                                    </Box>
                                    <FormControlLabel
                                      sx={{ m: 0 }}
                                      value={item.value}
                                      control={<Radio />}
                                      label=""
                                    />
                                  </Box>
                                ))}
                              </RadioGroup>
                            </Box>
                          </Box>
                          {errors.impactProgramsType && (
                            <Typography
                              sx={{ ...errorTextSx, mx: "auto", mt: 1 }}
                            >
                              {errors.impactProgramsType}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>
                  )}

                  {/* Submit */}
                  <Grid item xs={12}>
                    <Box
                      className="buttons"
                      sx={{ textAlign: "center", mt: 2, pb: "15px" }}
                    >
                      <Button
                        type="submit"
                        className="button-1"
                        sx={{
                          height: "50px",
                          border: "1.75px solid #54afe2",
                          borderRadius: "4px",
                          padding: "0 35px",
                          backgroundColor: "transparent",
                          color: "#62b5e5",
                          textTransform: "none",
                          fontWeight: 600,
                          ...baseText,
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: "#54afe2",
                            color: "#fff",
                          },
                        }}
                      >
                        {submitting ? "Sending..." : "SUBMIT"}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              >
                <Alert
                  severity={toast.sev}
                  onClose={() => setToast((t) => ({ ...t, open: false }))}
                  variant="filled"
                  sx={{ ...baseText }}
                >
                  {toast.msg}
                </Alert>
              </Snackbar>
            </Grid>
            <Grid item xs={12} md={0.5}></Grid>
            <Grid item xs={12} md={6}>
              <Typography>
                Find answers to common questions in our{" "}
                <a style={{ fontWeight: "bold" }} href="/faq">
                  FAQ
                </a>
              </Typography>
              <Divider sx={{ my: 2 }} />
              {!!warehouse && (
                <Box>
                  <Box>
                    <Typography>
                      For BillTrust, Invoices or Payments, call{" "}
                      <Link href="tel:8008688779" sx={linkSx}>
                        800-868-8779
                      </Link>
                      , Option 2 or
                    </Typography>
                    <Typography>
                      email{" "}
                      <Link href="mailto:Credit@LansingBP.com" sx={linkSx}>
                        Credit@LansingBP.com
                      </Link>
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography>
                    For product and order inquiries, contact your local branch:
                  </Typography>
                  <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                    Lansing Building Products - {warehouse.displayName}
                  </Typography>

                  <Typography sx={{ fontWeight: "bold", mt: 1 }}>
                    {warehouse.address}
                  </Typography>
                  {warehouse.phone && (
                    <Typography sx={{ noticeTextSx, mt: 1 }}>
                      <Link
                        href={`tel:${warehouse.phone.replace(/\D/g, "")}`}
                        sx={linkSx}
                      >
                        {formatPhone(warehouse.phone)}
                      </Link>
                    </Typography>
                  )}
                  {warehouse.email && (
                    <Typography sx={{ noticeTextSx, mt: 1 }}>
                      <Link href={`mailto:${warehouse.email}`} sx={linkSx}>
                        {warehouse.email}
                      </Link>
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>
          </Grid>
        </Box>
      )}
    </>
  );
}
